import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/use-toast';
import { BackButton } from '@/components/BackButton';
import { tenantPath, useTenantHref } from '@/lib/urls';
import { useTenantSlug, useTenantStore } from '@/stores/tenantStore';
import { useOrderEvents } from '@/hooks/useOrderEvents';
import { supabase } from '@/integrations/supabase/client';
import {
  exportPaymentsCsv,
  fetchPayments,
  reconcilePayments,
  refundPayment,
  requestPaymentReceipt,
  type FetchPaymentsParams,
  type PaymentRecord,
  type PaymentStatus,
} from '@/lib/api/payments';
import {
  AlertCircle,
  ArrowUpRight,
  Banknote,
  BarChart3,
  Calendar as CalendarIcon,
  CheckCircle2,
  CircleDot,
  Clock,
  CreditCard,
  DollarSign,
  Download,
  Eye,
  FileText,
  Loader2,
  Link as LinkIcon,
  MoreVertical,
  QrCode,
  Receipt,
  RefreshCw,
  RotateCcw,
  Search,
  Settings,
  TrendingDown,
  TrendingUp,
  Wallet,
  XCircle,
} from 'lucide-react';

type PaymentMethodCategory = 'card' | 'cash' | 'qr' | 'wallet' | 'other';

interface TransactionView {
  id: string;
  orderId: string;
  amountCents: number;
  method: string;
  methodCategory: PaymentMethodCategory;
  status: 'completed' | 'pending' | 'failed' | 'refunded';
  customerName: string;
  timestamp: Date;
  estimatedFeeCents: number;
  raw: PaymentRecord;
}

interface PaymentGateway {
  id: string;
  name: string;
  type: PaymentMethodCategory;
  provider: string;
  enabled: boolean;
  connected: boolean;
  description: string;
  processingFee: string;
}

const createDefaultDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 6);
  return {
    from: formatAsInputDate(from),
    to: formatAsInputDate(to),
  };
};

const formatAsInputDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toDateSafe = (value?: string | null) => {
  if (!value) {
    return undefined;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const normalizeStatus = (status: PaymentStatus): TransactionView['status'] => {
  switch (status) {
    case 'completed':
      return 'completed';
    case 'pending':
      return 'pending';
    case 'failed':
      return 'failed';
    case 'refunded':
    case 'voided':
      return 'refunded';
    default:
      return 'pending';
  }
};

const mapMethodCategory = (method?: string | null): PaymentMethodCategory => {
  const normalized = method?.toLowerCase() ?? '';

  if (normalized.includes('cash')) return 'cash';
  if (normalized.includes('qr') || normalized.includes('upi') || normalized.includes('scan')) return 'qr';
  if (normalized.includes('wallet') || normalized.includes('paytm') || normalized.includes('phonepe')) return 'wallet';
  if (
    normalized.includes('card') ||
    normalized.includes('credit') ||
    normalized.includes('debit') ||
    normalized.includes('visa') ||
    normalized.includes('mastercard') ||
    normalized.includes('stripe') ||
    normalized.includes('razorpay')
  ) {
    return 'card';
  }

  return 'other';
};

const estimateFeeCents = (amountCents: number, category: PaymentMethodCategory) => {
  if (category !== 'card') {
    return 0;
  }
  const variable = Math.round(amountCents * 0.029);
  const fixed = 30;
  return variable + fixed;
};

const formatCurrency = (cents: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format((cents ?? 0) / 100);

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const getPaymentMethodIcon = (method: PaymentMethodCategory) => {
  switch (method) {
    case 'card':
      return <CreditCard className="h-4 w-4" />;
    case 'cash':
      return <Banknote className="h-4 w-4" />;
    case 'qr':
      return <QrCode className="h-4 w-4" />;
    case 'wallet':
      return <Wallet className="h-4 w-4" />;
    default:
      return <DollarSign className="h-4 w-4" />;
  }
};

const getStatusBadge = (status: TransactionView['status']) => {
  const styles: Record<TransactionView['status'], string> = {
    completed: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600',
    pending: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600',
    failed: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600',
    refunded: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600',
  };

  const icons: Record<TransactionView['status'], JSX.Element> = {
    completed: <CheckCircle2 className="h-3 w-3" />,
    pending: <Clock className="h-3 w-3" />,
    failed: <XCircle className="h-3 w-3" />,
    refunded: <RotateCcw className="h-3 w-3" />,
  };

  return (
    <Badge variant="outline" className={`${styles[status]} flex items-center gap-1.5 px-2.5 py-1`}>
      {icons[status]}
      <span className="capitalize">{status}</span>
    </Badge>
  );
};

const BASE_GATEWAYS: PaymentGateway[] = [
  {
    id: 'stripe',
    name: 'Stripe',
    type: 'card',
    provider: 'Stripe Inc.',
    enabled: true,
    connected: true,
    description: 'Credit & debit cards',
    processingFee: '2.9% + $0.30',
  },
  {
    id: 'cash',
    name: 'Cash Drawer',
    type: 'cash',
    provider: 'Direct',
    enabled: true,
    connected: true,
    description: 'Physical cash transactions',
    processingFee: 'No fees',
  },
  {
    id: 'phonepe',
    name: 'UPI / QR',
    type: 'qr',
    provider: 'UPI Rails',
    enabled: false,
    connected: false,
    description: 'QR code & UPI payments',
    processingFee: '1.0%',
  },
  {
    id: 'paytm',
    name: 'Digital Wallet',
    type: 'wallet',
    provider: 'Wallet Provider',
    enabled: false,
    connected: false,
    description: 'Popular consumer wallets',
    processingFee: '1.5%',
  },
  {
    id: 'razorpay',
    name: 'Razorpay',
    type: 'card',
    provider: 'Razorpay',
    enabled: false,
    connected: false,
    description: 'Alternative card processor',
    processingFee: '2.5% + $0.25',
  },
];

const RANGE_PRESETS: Record<'today' | '7d' | '30d' | '90d', number> = {
  today: 0,
  '7d': 6,
  '30d': 29,
  '90d': 89,
};

const PAGE_SIZE = 100;
const CUSTOMER_HISTORY_LIMIT = 250;

const buildRangeFromPreset = (days: number) => {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  if (days === 0) {
    // keep same day
  } else {
    start.setDate(end.getDate() - days);
  }

  return {
    from: formatAsInputDate(start),
    to: formatAsInputDate(end),
  };
};

const normalizePhoneDigits = (value?: string | null) => {
  if (!value) {
    return '';
  }
  return value.replace(/\D/g, '');
};

const normalizeEmail = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const trimmed = value.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
};

interface CustomerMatchMetadata {
  idCandidates: string[];
  orderId: string | null;
  normalizedName: string | null;
  normalizedPhone: string | null;
  emailCandidates: string[];
}

const deriveCustomerMetadata = (payment: PaymentRecord): CustomerMatchMetadata => {
  const metadata =
    payment.metadata && typeof payment.metadata === 'object'
      ? (payment.metadata as Record<string, unknown>)
      : null;

  const readString = (key: string): string | null => {
    if (!metadata) {
      return null;
    }
    const value = metadata[key];
    return typeof value === 'string' ? value : null;
  };

  const idCandidates = [
    readString('customerId'),
    readString('customer_id'),
    readString('customerProfileId'),
    readString('customer_profile_id'),
    readString('customerProfileID'),
    readString('customer_profileID'),
  ]
    .filter((candidate): candidate is string => Boolean(candidate && candidate.trim().length > 0))
    .map((candidate) => candidate.trim());

  const phoneCandidate = readString('customerPhone') ?? readString('customer_phone');
  const normalizedPhone = phoneCandidate
    ? (() => {
        const digits = normalizePhoneDigits(phoneCandidate);
        return digits.length > 0 ? digits : null;
      })()
    : null;

  const emailCandidates = [
    normalizeEmail(readString('customerEmail')),
    normalizeEmail(readString('customer_email')),
    normalizeEmail(readString('guestEmail')),
    normalizeEmail(readString('guest_email')),
  ].filter((candidate): candidate is string => Boolean(candidate));

  const normalizedNameRaw = (payment.customerName ?? '').trim().toLowerCase();
  const normalizedName = normalizedNameRaw.length > 0 ? normalizedNameRaw : null;

  return {
    idCandidates,
    orderId: payment.orderId ?? null,
    normalizedName,
    normalizedPhone,
    emailCandidates,
  };
};

const mapPaymentRecordToTransactionView = (payment: PaymentRecord, fallbackIndex: number): TransactionView => {
  const normalizedStatus = normalizeStatus(payment.status ?? 'pending');
  const methodCategory = mapMethodCategory(payment.method);
  const amountCents = payment.amountCents ?? 0;
  const timestamp = toDateSafe(payment.createdAt) ?? new Date();
  const estimatedFeeCents = estimateFeeCents(amountCents, methodCategory);

  return {
    id: payment.id ?? `payment-${fallbackIndex}`,
    orderId: payment.orderId ?? payment.id ?? `order-${fallbackIndex}`,
    amountCents,
    method: payment.method ?? 'Unknown',
    methodCategory,
    status: normalizedStatus,
    customerName: payment.customerName ?? 'Guest',
    timestamp,
    estimatedFeeCents,
    raw: payment,
  };
};

const paymentMatchesCustomer = (payment: PaymentRecord, target: CustomerMatchMetadata): boolean => {
  const candidate = deriveCustomerMetadata(payment);

  if (target.orderId && candidate.orderId === target.orderId) {
    return true;
  }

  if (
    target.idCandidates.length > 0 &&
    candidate.idCandidates.some((value) => target.idCandidates.includes(value))
  ) {
    return true;
  }

  if (
    target.normalizedPhone &&
    candidate.normalizedPhone &&
    target.normalizedPhone === candidate.normalizedPhone
  ) {
    return true;
  }

  if (
    target.normalizedName &&
    candidate.normalizedName &&
    target.normalizedName === candidate.normalizedName
  ) {
    return true;
  }

  if (
    target.emailCandidates.length > 0 &&
    candidate.emailCandidates.some((value) => target.emailCandidates.includes(value))
  ) {
    return true;
  }

  return false;
};

const fetchCustomerHistoryPayments = async (
  params: FetchPaymentsParams,
  signal?: AbortSignal,
): Promise<PaymentRecord[]> => {
  const collected: PaymentRecord[] = [];
  const seen = new Set<string>();
  let cursor = params.cursor;

  while (true) {
    const response = await fetchPayments(
      { ...params, cursor, limit: params.limit ?? CUSTOMER_HISTORY_LIMIT },
      signal,
    );
    const payments = response.payments ?? [];
    payments.forEach((payment, index) => {
      const key = payment.id ?? `${payment.orderId ?? 'order'}-${collected.length + index}`;
      if (!seen.has(key)) {
        seen.add(key);
        collected.push(payment);
      }
    });

    if (!response.nextCursor || payments.length === 0) {
      break;
    }
    cursor = response.nextCursor ?? undefined;
  }

  return collected;
};

const PaymentsPage = () => {
  const { toast } = useToast();
  const { t } = useTranslation(['payments']);
  const queryClient = useQueryClient();
  const tenantSlug = useTenantSlug();
  const backHref = useTenantHref('dashboard');
  const activeRestaurantId = useTenantStore((state) => state.activeRestaurantId);

  useOrderEvents();

  const [dateRangePreset, setDateRangePreset] = useState<'today' | '7d' | '30d'>('7d');
  const [dateRange, setDateRange] = useState(createDefaultDateRange);
  const [customerHistoryRange, setCustomerHistoryRange] = useState<{ from?: string; to?: string }>(
    createDefaultDateRange(),
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | TransactionView['status']>('all');
  const [methodFilter, setMethodFilter] = useState<'all' | PaymentMethodCategory>('all');
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionView | null>(null);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isReconciling, setIsReconciling] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const [activeReceiptPaymentId, setActiveReceiptPaymentId] = useState<string | null>(null);
  const [customerHistoryTarget, setCustomerHistoryTarget] = useState<TransactionView | null>(null);
  const lastHistoryTargetId = useRef<string | null>(null);

  const defaultExportStart = useMemo(() => {
    const start = toDateSafe(dateRange.from);
    return start ?? new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  }, [dateRange.from]);

  const defaultExportEnd = useMemo(() => {
    const end = toDateSafe(dateRange.to);
    return end ?? new Date();
  }, [dateRange.to]);

  const [exportStartDate, setExportStartDate] = useState<Date | undefined>(defaultExportStart);
  const [exportEndDate, setExportEndDate] = useState<Date | undefined>(defaultExportEnd);

  useEffect(() => {
    setExportStartDate(defaultExportStart);
  }, [defaultExportStart]);

  useEffect(() => {
    setExportEndDate(defaultExportEnd);
  }, [defaultExportEnd]);

  useEffect(() => {
    const currentId = customerHistoryTarget?.id ?? null;
    if (currentId && currentId !== lastHistoryTargetId.current) {
      setCustomerHistoryRange({ from: dateRange.from, to: dateRange.to });
    }
    lastHistoryTargetId.current = currentId;
  }, [customerHistoryTarget?.id, dateRange.from, dateRange.to]);

  const buildTenantHref = useCallback(
    (page: string) => tenantPath(tenantSlug ?? undefined, page),
    [tenantSlug],
  );

  const fetchParams = useMemo<FetchPaymentsParams>(() => {
    const params: FetchPaymentsParams = {
      from: dateRange.from,
      to: dateRange.to,
    };
    if (statusFilter !== 'all') {
      params.status = statusFilter as PaymentStatus;
    }
    return params;
  }, [dateRange.from, dateRange.to, statusFilter]);

  const paymentsQuery = useInfiniteQuery({
    queryKey: ['payments', 'list', fetchParams],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      fetchPayments({ ...fetchParams, cursor: pageParam, limit: PAGE_SIZE }, signal),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const transactions = useMemo<TransactionView[]>(() => {
    const pages = paymentsQuery.data?.pages ?? [];
    if (!pages.length) {
      return [];
    }

    const mapped: TransactionView[] = [];
    pages.forEach((page) => {
      (page.payments ?? []).forEach((payment) => {
        mapped.push(mapPaymentRecordToTransactionView(payment, mapped.length));
      });
    });

    return mapped;
  }, [paymentsQuery.data]);

  const currency = transactions[0]?.raw.currency ?? 'USD';
  const summary = paymentsQuery.data?.pages?.[0]?.summary;

  const stats = useMemo(() => {
    const totalFeesCents = transactions.reduce((sum, t) => sum + t.estimatedFeeCents, 0);

    if (!summary) {
      return {
        totalRevenueCents: 0,
        totalTransactions: 0,
        completedCount: 0,
        pendingAmountCents: 0,
        pendingCount: 0,
        totalFeesCents,
        successRate: 0,
        refundedAmountCents: 0,
        refundedCount: 0,
      };
    }

    const successRate = summary.totalCount ? (summary.completedCount / summary.totalCount) * 100 : 0;

    return {
      totalRevenueCents: summary.completedAmountCents,
      totalTransactions: summary.totalCount,
      completedCount: summary.completedCount,
      pendingAmountCents: summary.pendingAmountCents,
      pendingCount: summary.pendingCount,
      totalFeesCents,
      successRate,
      refundedAmountCents: summary.refundedAmountCents,
      refundedCount: summary.refundedCount,
    };
  }, [summary, transactions]);


  const paymentMethodStats = useMemo(() => {
    const base: Record<PaymentMethodCategory, number> = {
      card: 0,
      cash: 0,
      qr: 0,
      wallet: 0,
      other: 0,
    };

    transactions.forEach((transaction) => {
      base[transaction.methodCategory] += transaction.amountCents;
    });

    return base;
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return transactions.filter((transaction) => {
      const matchesSearch =
        !query ||
        transaction.id.toLowerCase().includes(query) ||
        transaction.orderId.toLowerCase().includes(query) ||
        transaction.customerName.toLowerCase().includes(query);

      const matchesStatus = statusFilter === 'all' || transaction.status === statusFilter;
      const matchesMethod = methodFilter === 'all' || transaction.methodCategory === methodFilter;

      return matchesSearch && matchesStatus && matchesMethod;
    });
  }, [transactions, searchQuery, statusFilter, methodFilter]);

  const customerHistoryQuery = useQuery({
    enabled: Boolean(customerHistoryTarget),
    queryKey: [
      'payments',
      'customer-history',
      customerHistoryTarget?.id ?? '__none__',
      customerHistoryRange.from ?? '__open__',
      customerHistoryRange.to ?? '__open__',
    ],
    queryFn: async ({ signal }) => {
      if (!customerHistoryTarget) {
        return [];
      }

      const payments = await fetchCustomerHistoryPayments(
        { ...customerHistoryRange, limit: CUSTOMER_HISTORY_LIMIT, status: 'all' },
        signal,
      );
      const targetMetadata = deriveCustomerMetadata(customerHistoryTarget.raw);

      const uniquePayments = new Map<string, PaymentRecord>();
      const addPayment = (payment: PaymentRecord, fallbackIndex: number) => {
        const key = payment.id ?? `${payment.orderId ?? 'order'}-${fallbackIndex}`;
        if (!uniquePayments.has(key)) {
          uniquePayments.set(key, payment);
        }
      };

      payments.forEach((payment, index) => {
        if (paymentMatchesCustomer(payment, targetMetadata)) {
          addPayment(payment, index);
        }
      });

      addPayment(customerHistoryTarget.raw, payments.length + 1);

      const mapped = Array.from(uniquePayments.values()).map((payment, index) =>
        mapPaymentRecordToTransactionView(payment, index),
      );

      return mapped.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    },
    staleTime: 30_000,
  });

  const customerHistoryTransactions = useMemo(
    () => customerHistoryQuery.data ?? [],
    [customerHistoryQuery.data],
  );
  const customerHistorySummary = useMemo(() => {
    if (!customerHistoryTransactions.length) {
      return { count: 0, totalCents: 0, lastVisit: null as Date | null };
    }

    const totalCents = customerHistoryTransactions.reduce(
      (sum, transaction) => sum + transaction.amountCents,
      0,
    );
    const lastVisit = customerHistoryTransactions.reduce<Date | null>((latest, transaction) => {
      if (!latest || transaction.timestamp > latest) {
        return transaction.timestamp;
      }
      return latest;
    }, null);

    return {
      count: customerHistoryTransactions.length,
      totalCents,
      lastVisit,
    };
  }, [customerHistoryTransactions]);

  const historyCustomerName =
    customerHistoryTarget?.customerName?.trim() ||
    t('payments:historySheet.unknownCustomer', { defaultValue: 'Guest' });

  const [gateways, setGateways] = useState<PaymentGateway[]>(BASE_GATEWAYS);

  useEffect(() => {
    setGateways((prev) =>
      prev.map((gateway) => {
        const amountForMethod = paymentMethodStats[gateway.type] ?? 0;
        const connected = amountForMethod > 0;

        if (!connected) {
          return { ...gateway, connected: false, enabled: false };
        }

        return { ...gateway, connected: true, enabled: gateway.enabled || connected };
      }),
    );
  }, [paymentMethodStats]);

  useEffect(() => {
    if (!activeRestaurantId) {
      return;
    }

    const channel = supabase
      .channel(`payments-live-${activeRestaurantId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payments',
          filter: `restaurant_id=eq.${activeRestaurantId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['payments', 'list'] });
          if (import.meta.env?.DEV) {
            console.debug('[PaymentsPage] realtime payments update');
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeRestaurantId, queryClient]);

  const handleRangePresetChange = useCallback(
    (value: 'today' | '7d' | '30d') => {
      setDateRangePreset(value);
      const preset = RANGE_PRESETS[value];
      const nextRange = buildRangeFromPreset(preset);
      setDateRange(nextRange);
    },
    [],
  );

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['payments', 'list'] });
  }, [queryClient]);

  const getExportDaysCount = useCallback(() => {
    if (!exportStartDate || !exportEndDate) {
      return 0;
    }
    return Math.ceil((exportEndDate.getTime() - exportStartDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  }, [exportStartDate, exportEndDate]);

  const handleExportTransactions = useCallback(async () => {
    if (!exportStartDate || !exportEndDate) {
      toast({
        title: 'Select a date range',
        description: 'Choose both start and end dates to export transactions.',
        variant: 'destructive',
      });
      return;
    }

    if (exportStartDate > exportEndDate) {
      toast({
        title: 'Invalid date range',
        description: 'Start date must be before end date.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsExporting(true);
      const params = {
        from: formatAsInputDate(exportStartDate),
        to: formatAsInputDate(exportEndDate),
        status: statusFilter === 'all' ? undefined : (statusFilter as PaymentStatus),
      };

      const blob = await exportPaymentsCsv(params);

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `payments-${params.from}-to-${params.to}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);

      const daysDiff = getExportDaysCount();

      toast({
        title: 'Export complete',
        description: `Downloaded ${daysDiff} day${daysDiff === 1 ? '' : 's'} of transactions as CSV.`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Export failed. Please try again.';
      toast({
        title: 'Export failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  }, [exportEndDate, exportStartDate, getExportDaysCount, statusFilter, toast]);

  const handleQuickRange = useCallback((days: number) => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end);
    if (days === 0) {
      start.setHours(0, 0, 0, 0);
    } else {
      start.setDate(end.getDate() - (days - 1));
      start.setHours(0, 0, 0, 0);
    }
    setExportStartDate(start);
    setExportEndDate(end);
  }, []);

  const handleReconciliation = useCallback(async () => {
    try {
      setIsReconciling(true);
      const result = await reconcilePayments({ from: dateRange.from, to: dateRange.to });
      const difference = result.run.totals.differenceCents;
      const orderMismatches = result.run.unmatchedOrders.length;
      const paymentMismatches = result.run.unmatchedPayments.length;

      toast({
        title: difference === 0 ? 'Reconciliation balanced' : 'Reconciliation completed',
        description:
          difference === 0
            ? 'No discrepancies detected between orders and payments.'
            : `${paymentMismatches} payments and ${orderMismatches} orders need review (variance ${formatCurrency(
                difference,
                currency,
              )}).`,
        variant: difference === 0 ? 'default' : 'default',
      });
      queryClient.invalidateQueries({ queryKey: ['payments', 'list'] });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Reconciliation failed. Please try again.';
      toast({
        title: 'Reconciliation error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsReconciling(false);
    }
  }, [currency, dateRange.from, dateRange.to, queryClient, toast]);

  const handleRefund = useCallback(async () => {
    if (!selectedTransaction || !refundReason.trim()) {
      toast({
        title: 'Refund requires a reason',
        description: 'Please add a short note before processing the refund.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsRefunding(true);
      await refundPayment(selectedTransaction.id, {
        reason: refundReason.trim(),
        note: refundReason.trim(),
      });

      toast({
        title: 'Refund processed',
        description: `${formatCurrency(
          selectedTransaction.amountCents,
          currency,
        )} will be returned to ${selectedTransaction.customerName}.`,
      });

      setShowRefundDialog(false);
      setRefundReason('');
      setSelectedTransaction(null);
      queryClient.invalidateQueries({ queryKey: ['payments', 'list'] });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'We were unable to process the refund.';
      toast({
        title: 'Refund failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsRefunding(false);
    }
  }, [currency, queryClient, refundReason, selectedTransaction, toast]);

  const downloadReceiptFile = useCallback((delivery: { filename: string; mimeType?: string; data: string; size?: number }) => {
    try {
      const binary = atob(delivery.data);
      const buffer = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index += 1) {
        buffer[index] = binary.charCodeAt(index);
      }
      const blob = new Blob([buffer], { type: delivery.mimeType ?? 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = delivery.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('[PaymentsPage] unable to download receipt file', error);
      toast({
        title: 'Download failed',
        description: 'We could not save the receipt file. Please try again.',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const handleReceiptAction = useCallback(
    async (transaction: TransactionView, delivery: 'email' | 'download') => {
      try {
        setActiveReceiptPaymentId(transaction.id);
        const result = await requestPaymentReceipt(transaction.id, { delivery });

        if (delivery === 'download' && result.delivery?.type === 'download') {
          downloadReceiptFile(result.delivery);
          toast({
            title: 'Receipt downloaded',
            description: `${result.delivery.filename} saved to your device.`,
          });
          return;
        }

        const recipient = result.delivery?.type === 'email' ? result.delivery.to : transaction.customerName ?? 'customer';
        toast({
          title: 'Receipt sent',
          description: `Receipt delivered to ${recipient}.`,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to complete the receipt request.';
        toast({
          title: 'Receipt request failed',
          description: message,
          variant: 'destructive',
        });
      } finally {
        setActiveReceiptPaymentId(null);
      }
    },
    [downloadReceiptFile, toast],
  );

  const toggleGateway = useCallback(
    (id: string) => {
      setGateways((prev) =>
        prev.map((gateway) => {
          if (gateway.id !== id) {
            return gateway;
          }

          const nextEnabled = !gateway.enabled;
          toast({
            title: nextEnabled ? `${gateway.name} enabled` : `${gateway.name} disabled`,
            description: nextEnabled
              ? 'Transactions using this gateway will sync automatically.'
              : 'We will pause syncing new transactions from this gateway.',
          });

          return { ...gateway, enabled: nextEnabled };
        }),
      );
    },
    [toast],
  );

  const connectGateway = useCallback(
    (id: string) => {
      const gateway = gateways.find((g) => g.id === id);
      if (!gateway) return;

      if (gateway.connected) {
        toast({
          title: `${gateway.name} already connected`,
          description: 'You can configure advanced settings or disconnect if required.',
        });
        return;
      }

      toast({
        title: `Redirecting to ${gateway.name}`,
        description: 'Complete the connection flow with your provider. We will sync new payments instantly.',
      });
    },
    [gateways, toast],
  );

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleStatusFilterChange = useCallback((value: TransactionView['status'] | 'all') => {
    setStatusFilter(value);
  }, []);

  const handleMethodFilterChange = useCallback((value: PaymentMethodCategory | 'all') => {
    setMethodFilter(value);
  }, []);

  const historyRangeFromDate = useMemo(
    () => toDateSafe(customerHistoryRange.from),
    [customerHistoryRange.from],
  );
  const historyRangeToDate = useMemo(
    () => toDateSafe(customerHistoryRange.to),
    [customerHistoryRange.to],
  );

  const handleCustomerHistoryDateSelect = useCallback(
    (edge: 'from' | 'to', date?: Date) => {
      setCustomerHistoryRange((prev) => {
        const next: { from?: string; to?: string } = {
          ...prev,
          [edge]: date ? formatAsInputDate(date) : undefined,
        };
        if (next.from && next.to) {
          const fromDate = toDateSafe(next.from);
          const toDate = toDateSafe(next.to);
          if (fromDate && toDate && fromDate > toDate) {
            if (edge === 'from') {
              next.to = next.from;
            } else {
              next.from = next.to;
            }
          }
        }
        return next;
      });
    },
    [],
  );

  const handleCustomerHistoryPreset = useCallback(
    (preset: 'today' | '7d' | '30d' | '90d' | 'all') => {
      if (preset === 'all') {
        setCustomerHistoryRange({ from: undefined, to: undefined });
        return;
      }

      if (preset === 'today') {
        setCustomerHistoryRange(buildRangeFromPreset(RANGE_PRESETS.today));
        return;
      }

      const presetMap: Record<'7d' | '30d' | '90d', number> = {
        '7d': RANGE_PRESETS['7d'],
        '30d': RANGE_PRESETS['30d'],
        '90d': RANGE_PRESETS['90d'],
      };
      setCustomerHistoryRange(buildRangeFromPreset(presetMap[preset]));
    },
    [],
  );

  const formatTime = (date: Date) =>
    date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });

  const formatDate = (date: Date) =>
    date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

  const formatDateShort = (date?: Date) => {
    if (!date) return 'Select';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const lastUpdated = paymentsQuery.dataUpdatedAt
    ? new Date(paymentsQuery.dataUpdatedAt).toLocaleTimeString()
    : 'Just now';

  const isLoading = paymentsQuery.isLoading;
  const isError = paymentsQuery.isError;
  const fetchNextPage = paymentsQuery.fetchNextPage;
  const isFetching = paymentsQuery.isFetching;
  const isFetchingNextPage = paymentsQuery.isFetchingNextPage;
  const hasNextPage = paymentsQuery.hasNextPage;
  const isRefreshing = isFetching && !isFetchingNextPage && !isLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto flex max-w-6xl flex-col gap-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-2xl" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-64 rounded-full" />
                <Skeleton className="h-4 w-40 rounded-full" />
              </div>
            </div>
            <Skeleton className="h-12 w-full rounded-2xl" />
          </div>
          <div className="grid gap-4 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <Card key={index} className="border-muted">
                <CardContent className="space-y-3 p-6">
                  <Skeleton className="h-4 w-24 rounded-full" />
                  <Skeleton className="h-8 w-32 rounded-lg" />
                  <Skeleton className="h-4 w-20 rounded-full" />
                </CardContent>
              </Card>
            ))}
          </div>
          <Card className="border-muted">
            <CardHeader className="border-b border-muted/60">
              <Skeleton className="h-5 w-48 rounded-full" />
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-12 w-full rounded-lg" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isError) {
    const message =
      (paymentsQuery.error as Error)?.message ?? 'We could not load payments for this range yet.';
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-xl">
          <Card className="border-destructive/40 bg-destructive/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                Payments unavailable
              </CardTitle>
              <CardDescription>{message}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <Button onClick={handleRefresh} variant="outline" className="w-fit">
                <RefreshCw className="mr-2 h-4 w-4" />
                Try again
              </Button>
              <p className="text-xs text-muted-foreground">
                We will keep watching for new payments and refresh automatically.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-6 pb-8 pt-2 sm:px-8 lg:px-10 max-w-[1600px] mx-auto">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-4">
          <BackButton to={backHref} text="Back to Dashboard" />
          <div className="space-y-1.5">
            <h1 className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-700 bg-clip-text text-3xl font-bold tracking-tight text-transparent dark:from-white dark:via-gray-100 dark:to-gray-300">
              Payment Analytics
            </h1>
            <p className="text-muted-foreground">
              Real-time transaction monitoring and revenue insights
            </p>
            <p className="text-xs text-muted-foreground">Last updated {lastUpdated}</p>
          </div>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Select value={dateRangePreset} onValueChange={(value) => handleRangePresetChange(value as typeof dateRangePreset)}>
            <SelectTrigger className="h-11 w-full sm:w-40">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={handleReconciliation}
            disabled={isReconciling}
            className="gap-2 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
          >
            {isReconciling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {isReconciling ? 'Reconciling...' : 'Reconcile'}
          </Button>
        </div>
      </div>

      <div className="grid auto-rows-fr grid-cols-1 gap-5 md:grid-cols-4">
          <Card className="group relative h-full overflow-hidden border-gray-200/60 bg-gradient-to-br from-white to-gray-50/50 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-gray-700/60 dark:from-gray-900 dark:to-gray-900/50">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100 dark:from-emerald-500/10" />
            <CardContent className="relative flex h-full flex-col justify-between p-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="mb-2 text-sm font-medium text-muted-foreground">Total Revenue</p>
                  <div className="text-4xl font-bold tracking-tight">
                    {formatCurrency(stats.totalRevenueCents, currency)}
                  </div>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-50 p-3.5 shadow-inner transition-transform duration-300 group-hover:scale-110 dark:from-emerald-900/30 dark:to-emerald-900/10">
                  <DollarSign className="h-6 w-6 text-emerald-700 dark:text-emerald-400" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center gap-1 rounded-lg bg-emerald-500/10 px-2.5 py-1 dark:bg-emerald-500/20">
                  <TrendingUp className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                    {formatPercent(stats.successRate || 0)}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">Success rate across the period</span>
              </div>
            </CardContent>
          </Card>

          <Card className="group relative h-full overflow-hidden border-gray-200/60 bg-gradient-to-br from-white to-gray-50/50 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-gray-700/60 dark:from-gray-900 dark:to-gray-900/50">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100 dark:from-blue-500/10" />
            <CardContent className="relative flex h-full flex-col justify-between p-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="mb-2 text-sm font-medium text-muted-foreground">Transactions</p>
                  <div className="text-4xl font-bold tracking-tight">{stats.totalTransactions}</div>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-blue-100 to-blue-50 p-3.5 shadow-inner transition-transform duration-300 group-hover:scale-110 dark:from-blue-900/30 dark:to-blue-900/10">
                  <Receipt className="h-6 w-6 text-blue-700 dark:text-blue-400" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center gap-1 rounded-lg bg-blue-500/10 px-2.5 py-1 dark:bg-blue-500/20">
                  <span className="text-sm font-semibold text-blue-700 dark:text-blue-400">
                    {stats.completedCount} completed
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">Across the selected range</span>
              </div>
            </CardContent>
          </Card>
          <Card className="group relative h-full overflow-hidden border-orange-200/60 bg-gradient-to-br from-orange-50/50 to-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-orange-700/30 dark:from-orange-900/10 dark:to-gray-900">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100 dark:from-orange-500/10" />
            <CardContent className="relative flex h-full flex-col justify-between p-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="mb-2 text-sm font-medium text-muted-foreground">Pending</p>
                  <div className="text-4xl font-bold tracking-tight text-orange-700 dark:text-orange-400">
                    {formatCurrency(stats.pendingAmountCents, currency)}
                  </div>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-orange-100 to-orange-50 p-3.5 shadow-inner transition-transform duration-300 group-hover:scale-110 dark:from-orange-900/30 dark:to-orange-900/10">
                  <Clock className="h-6 w-6 text-orange-700 dark:text-orange-400" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {stats.pendingCount} transaction{stats.pendingCount === 1 ? '' : 's'} awaiting
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="group relative h-full overflow-hidden border-gray-200/60 bg-gradient-to-br from-white to-gray-50/50 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-gray-700/60 dark:from-gray-900 dark:to-gray-900/50">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100 dark:from-purple-500/10" />
            <CardContent className="relative flex h-full flex-col justify-between p-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <p className="mb-2 text-sm font-medium text-muted-foreground">Processing Fees</p>
                  <div className="text-4xl font-bold tracking-tight">
                    {formatCurrency(stats.totalFeesCents, currency)}
                  </div>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-purple-100 to-purple-50 p-3.5 shadow-inner transition-transform duration-300 group-hover:scale-110 dark:from-purple-900/30 dark:to-purple-900/10">
                  <BarChart3 className="h-6 w-6 text-purple-700 dark:text-purple-400" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center gap-1 rounded-lg bg-purple-500/10 px-2.5 py-1 dark:bg-purple-500/20">
                  <TrendingDown className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                  <span className="text-sm font-semibold text-purple-700 dark:text-purple-400">
                    {stats.totalRevenueCents
                      ? formatPercent((stats.totalFeesCents / stats.totalRevenueCents) * 100)
                      : '0.0%'}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">of completed revenue</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="transactions" className="space-y-8">
          <div className="flex items-center justify-center">
            <div className="relative inline-flex items-center rounded-[2rem] border border-gray-200/60 bg-gradient-to-br from-gray-50 to-gray-100 p-1 shadow-[0_8px_32px_rgba(0,0,0,0.08)] dark:border-gray-700/40 dark:from-gray-800 dark:to-gray-900 dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
              <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              <TabsList className="relative inline-flex h-[3.25rem] items-center gap-1.5 rounded-[1.75rem] bg-transparent p-1">
                <TabsTrigger
                  value="transactions"
                  className="group relative rounded-[1.5rem] px-8 py-3 text-sm font-semibold transition-all duration-300 ease-out data-[state=active]:scale-[1.02] data-[state=active]:bg-gradient-to-br data-[state=active]:from-gray-900 data-[state=active]:via-gray-800 data-[state=active]:to-gray-900 data-[state=active]:text-white data-[state=active]:shadow-[0_8px_24px_rgba(0,0,0,0.2),0_2px_8px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.1)] data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/60 data-[state=inactive]:hover:text-gray-900 dark:data-[state=active]:from-gray-100 dark:data-[state=active]:via-white dark:data-[state=active]:to-gray-100 dark:data-[state=active]:text-gray-900 dark:data-[state=active]:shadow-[0_8px_24px_rgba(255,255,255,0.15),0_2px_8px_rgba(255,255,255,0.1),inset_0_1px_0_rgba(0,0,0,0.05)] dark:data-[state=inactive]:text-gray-400 dark:data-[state=inactive]:hover:bg-gray-800/60 dark:data-[state=inactive]:hover:text-gray-100"
                >
                  <span className="absolute inset-0 rounded-[1.5rem] bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 opacity-0 blur-xl transition-opacity duration-500 group-data-[state=active]:opacity-100" />
                  <span className="relative flex items-center gap-2">
                    <span className="relative">
                      Transactions
                      <span className="absolute -bottom-1 left-0 h-[2px] w-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300 group-data-[state=active]:w-full" />
                    </span>
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="breakdown"
                  className="group relative rounded-[1.5rem] px-8 py-3 text-sm font-semibold transition-all duration-300 ease-out data-[state=active]:scale-[1.02] data-[state=active]:bg-gradient-to-br data-[state=active]:from-gray-900 data-[state=active]:via-gray-800 data-[state=active]:to-gray-900 data-[state=active]:text-white data-[state=active]:shadow-[0_8px_24px_rgba(0,0,0,0.2),0_2px_8px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.1)] data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/60 data-[state=inactive]:hover:text-gray-900 dark:data-[state=active]:from-gray-100 dark:data-[state=active]:via-white dark:data-[state=active]:to-gray-100 dark:data-[state=active]:text-gray-900 dark:data-[state=active]:shadow-[0_8px_24px_rgba(255,255,255,0.15),0_2px_8px_rgba(255,255,255,0.1),inset_0_1px_0_rgba(0,0,0,0.05)] dark:data-[state=inactive]:text-gray-400 dark:data-[state=inactive]:hover:bg-gray-800/60 dark:data-[state=inactive]:hover:text-gray-100"
                >
                  <span className="absolute inset-0 rounded-[1.5rem] bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 opacity-0 blur-xl transition-opacity duration-500 group-data-[state=active]:opacity-100" />
                  <span className="relative flex items-center gap-2">
                    <span className="relative">
                      Payment Methods
                      <span className="absolute -bottom-1 left-0 h-[2px] w-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300 group-data-[state=active]:w-full" />
                    </span>
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="gateways"
                  className="group relative rounded-[1.5rem] px-8 py-3 text-sm font-semibold transition-all duration-300 ease-out data-[state=active]:scale-[1.02] data-[state=active]:bg-gradient-to-br data-[state=active]:from-gray-900 data-[state=active]:via-gray-800 data-[state=active]:to-gray-900 data-[state=active]:text-white data-[state=active]:shadow-[0_8px_24px_rgba(0,0,0,0.2),0_2px_8px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.1)] data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/60 data-[state=inactive]:hover:text-gray-900 dark:data-[state=active]:from-gray-100 dark:data-[state=active]:via-white dark:data-[state=active]:to-gray-100 dark:data-[state=active]:text-gray-900 dark:data-[state=active]:shadow-[0_8px_24px_rgba(255,255,255,0.15),0_2px_8px_rgba(255,255,255,0.1),inset_0_1px_0_rgba(0,0,0,0.05)] dark:data-[state=inactive]:text-gray-400 dark:data-[state=inactive]:hover:bg-gray-800/60 dark:data-[state=inactive]:hover:text-gray-100"
                >
                  <span className="absolute inset-0 rounded-[1.5rem] bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 opacity-0 blur-xl transition-opacity duration-500 group-data-[state=active]:opacity-100" />
                  <span className="relative flex items-center gap-2">
                    <span className="relative">
                      Gateways
                      <span className="absolute -bottom-1 left-0 h-[2px] w-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300 group-data-[state=active]:w-full" />
                    </span>
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="settlement"
                  className="group relative rounded-[1.5rem] px-8 py-3 text-sm font-semibold transition-all duration-300 ease-out data-[state=active]:scale-[1.02] data-[state=active]:bg-gradient-to-br data-[state=active]:from-gray-900 data-[state=active]:via-gray-800 data-[state=active]:to-gray-900 data-[state=active]:text-white data-[state=active]:shadow-[0_8px_24px_rgba(0,0,0,0.2),0_2px_8px_rgba(0,0,0,0.15),inset_0_1px_0_rgba(255,255,255,0.1)] data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/60 data-[state=inactive]:hover:text-gray-900 dark:data-[state=active]:from-gray-100 dark:data-[state=active]:via-white dark:data-[state=active]:to-gray-100 dark:data-[state=active]:text-gray-900 dark:data-[state=active]:shadow-[0_8px_24px_rgba(255,255,255,0.15),0_2px_8px_rgba(255,255,255,0.1),inset_0_1px_0_rgba(0,0,0,0.05)] dark:data-[state=inactive]:text-gray-400 dark:data-[state=inactive]:hover:bg-gray-800/60 dark:data-[state=inactive]:hover:text-gray-100"
                >
                  <span className="absolute inset-0 rounded-[1.5rem] bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 opacity-0 blur-xl transition-opacity duration-500 group-data-[state=active]:opacity-100" />
                  <span className="relative flex items-center gap-2">
                    <span className="relative">
                      Settlement
                      <span className="absolute -bottom-1 left-0 h-[2px] w-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300 group-data-[state=active]:w-full" />
                    </span>
                  </span>
                </TabsTrigger>
              </TabsList>
            </div>
          </div>

          <TabsContent value="transactions" className="space-y-6">
            <Card className="border-gray-200/60 bg-white shadow-sm dark:border-gray-700/60 dark:bg-gray-900">
              <CardContent className="pt-6">
                <div className="flex flex-col gap-4 md:flex-row">
                  <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search transactions..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="pl-10"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={(value) => handleStatusFilterChange(value as typeof statusFilter)}>
                    <SelectTrigger className="w-full md:w-40">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="refunded">Refunded</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={methodFilter} onValueChange={(value) => handleMethodFilterChange(value as typeof methodFilter)}>
                    <SelectTrigger className="w-full md:w-40">
                      <SelectValue placeholder="Method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Methods</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="qr">QR Code</SelectItem>
                      <SelectItem value="wallet">Wallet</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200/60 bg-white shadow-sm dark:border-gray-700/60 dark:bg-gray-900">
              <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Recent Transactions</CardTitle>
                    <CardDescription>{filteredTransactions.length} transactions found</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="gap-2"
                  >
                    {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    {isRefreshing ? 'Refreshing...' : 'Sync now'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {filteredTransactions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
                    <div className="rounded-2xl bg-muted p-6">
                      <CreditCard className="h-10 w-10 text-muted-foreground" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="text-lg font-semibold">No transactions match the filters</h3>
                      <p className="text-sm text-muted-foreground">
                        Adjust search criteria or reduce filters to see more results.
                      </p>
                    </div>
                    {hasNextPage && (
                      <Button
                        variant="outline"
                        onClick={() => fetchNextPage()}
                        disabled={isFetchingNextPage}
                        className="gap-2"
                      >
                        {isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {isFetchingNextPage ? 'Loading...' : 'Load more'}
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                      <TableHeader>
                        <TableRow className="border-gray-200/60 hover:bg-transparent dark:border-gray-700/60">
                          <TableHead className="font-semibold pl-6">Transaction</TableHead>
                          <TableHead className="font-semibold">Customer</TableHead>
                          <TableHead className="font-semibold">Amount</TableHead>
                          <TableHead className="font-semibold">Method</TableHead>
                          <TableHead className="font-semibold">Status</TableHead>
                          <TableHead className="font-semibold">Time</TableHead>
                          <TableHead className="font-semibold w-[90px] pr-6">
                            <div className="flex items-center justify-end">Actions</div>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredTransactions.map((transaction) => {
                          const feeDisplay =
                            transaction.estimatedFeeCents > 0
                              ? formatCurrency(transaction.estimatedFeeCents, currency)
                              : null;
                          const orderHref = buildTenantHref(`orders/${transaction.orderId}`);

                          return (
                            <TableRow
                              key={transaction.id}
                              className="group cursor-pointer border-gray-200/60 transition-colors hover:bg-gray-50/50 dark:border-gray-700/60 dark:hover:bg-gray-800/50"
                              onClick={() => setSelectedTransaction(transaction)}
                            >
                              <TableCell className="pl-6">
                                <div className="flex flex-col">
                                  <span className="text-sm font-semibold">{transaction.id}</span>
                                  <Link
                                    to={orderHref}
                                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <ArrowUpRight className="h-3.5 w-3.5" />
                                    #{transaction.orderId}
                                  </Link>
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm font-medium">{transaction.customerName}</span>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col">
                                  <span className="font-semibold">
                                    {formatCurrency(transaction.amountCents, currency)}
                                  </span>
                                  {feeDisplay && (
                                    <span className="text-xs text-muted-foreground">Fee: {feeDisplay}</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="inline-flex items-center gap-2 rounded-md bg-gray-100 px-2.5 py-1.5 text-sm capitalize dark:bg-gray-800">
                                  {getPaymentMethodIcon(transaction.methodCategory)}
                                  {transaction.methodCategory}
                                </div>
                              </TableCell>
                              <TableCell>{getStatusBadge(transaction.status)}</TableCell>
                              <TableCell>
                                <div className="flex flex-col">
                                  <span className="text-sm">{formatTime(transaction.timestamp)}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {formatDate(transaction.timestamp)}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="pr-6">
                                <div className="flex items-center justify-end">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                                        onClick={(event) => event.stopPropagation()}
                                      >
                                        <MoreVertical className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                      <DropdownMenuItem
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          setSelectedTransaction(transaction);
                                        }}
                                      >
                                        <Eye className="mr-2 h-4 w-4" />
                                        View details
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        disabled={activeReceiptPaymentId === transaction.id}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleReceiptAction(transaction, 'email');
                                        }}
                                      >
                                        {activeReceiptPaymentId === transaction.id ? (
                                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : (
                                          <Receipt className="mr-2 h-4 w-4" />
                                        )}
                                        Email receipt
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        disabled={activeReceiptPaymentId === transaction.id}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleReceiptAction(transaction, 'download');
                                        }}
                                      >
                                        {activeReceiptPaymentId === transaction.id ? (
                                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : (
                                          <Download className="mr-2 h-4 w-4" />
                                        )}
                                        Download receipt
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          setCustomerHistoryTarget(transaction);
                                        }}
                                      >
                                        <BarChart3 className="mr-2 h-4 w-4" />
                                        Customer history
                                      </DropdownMenuItem>
                                      {transaction.status === 'completed' && (
                                        <DropdownMenuItem
                                          className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            setSelectedTransaction(transaction);
                                            setShowRefundDialog(true);
                                          }}
                                        >
                                          <RotateCcw className="mr-2 h-4 w-4" />
                                          Issue refund
                                        </DropdownMenuItem>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    </div>
                    {hasNextPage && (
                      <div className="border-t border-gray-200/60 px-6 py-4 text-center dark:border-gray-700/60">
                        <Button
                          variant="outline"
                          onClick={() => fetchNextPage()}
                          disabled={isFetchingNextPage}
                          className="gap-2"
                        >
                          {isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                          {isFetchingNextPage ? 'Loading...' : 'Load more'}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="breakdown" className="space-y-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Card className="border-gray-200/60 bg-white shadow-sm dark:border-gray-700/60 dark:bg-gray-900">
                <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
                  <CardTitle>Payment Methods</CardTitle>
                  <CardDescription>Revenue distribution by payment type</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-6">
                  {(['card', 'cash', 'qr', 'wallet', 'other'] as PaymentMethodCategory[]).map((method) => {
                    const amountCents = paymentMethodStats[method] ?? 0;
                    const percentage =
                      stats.totalRevenueCents > 0
                        ? ((amountCents / stats.totalRevenueCents) * 100).toFixed(1)
                        : '0.0';
                    return (
                      <div key={method} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="rounded-lg bg-gray-100 p-2 dark:bg-gray-800">
                              {getPaymentMethodIcon(method)}
                            </div>
                            <div>
                              <p className="font-medium capitalize">{method}</p>
                              <p className="text-xs text-muted-foreground">{percentage}% of total</p>
                            </div>
                          </div>
                          <p className="font-semibold">{formatCurrency(amountCents, currency)}</p>
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-gray-600 to-gray-700 transition-all duration-500 dark:from-gray-400 dark:to-gray-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <Card className="border-gray-200/60 bg-white shadow-sm dark:border-gray-700/60 dark:bg-gray-900">
                <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
                  <CardTitle>Transaction Timeline</CardTitle>
                  <CardDescription>Recent payment activity</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-6">
                  {transactions.slice(0, 6).map((transaction) => (
                    <div key={transaction.id} className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                        {getPaymentMethodIcon(transaction.methodCategory)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-medium">{transaction.customerName}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatTime(transaction.timestamp)} - {transaction.methodCategory}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold">
                              {formatCurrency(transaction.amountCents, currency)}
                            </p>
                            <div className="flex justify-end">
                              {transaction.status === 'completed' && (
                                <CheckCircle2 className="h-3 w-3 text-muted-foreground" />
                              )}
                              {transaction.status === 'pending' && (
                                <Clock className="h-3 w-3 text-muted-foreground" />
                              )}
                              {transaction.status === 'failed' && (
                                <XCircle className="h-3 w-3 text-muted-foreground" />
                              )}
                              {transaction.status === 'refunded' && (
                                <RotateCcw className="h-3 w-3 text-muted-foreground" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="gateways" className="space-y-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {gateways.map((gateway) => (
                <Card
                  key={gateway.id}
                  className="group border-gray-200/60 bg-white shadow-sm transition-all duration-300 hover:shadow-md dark:border-gray-700/60 dark:bg-gray-900"
                >
                  <CardHeader className="border-b border-gray-200/60 dark:border-gray-700/60">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="rounded-lg bg-gray-100 p-2.5 dark:bg-gray-800">
                          {getPaymentMethodIcon(gateway.type)}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{gateway.name}</CardTitle>
                          <CardDescription>{gateway.description}</CardDescription>
                        </div>
                      </div>
                      <Switch
                        checked={gateway.enabled && gateway.connected}
                        disabled={!gateway.connected}
                        onCheckedChange={() => toggleGateway(gateway.id)}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Provider</span>
                      <span className="font-medium">{gateway.provider}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Processing Fee</span>
                      <span className="font-medium">{gateway.processingFee}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Status</span>
                      <Badge
                        variant="outline"
                        className="flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs dark:bg-gray-800"
                      >
                        <CircleDot className={`h-2 w-2 ${gateway.connected ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                        {gateway.connected ? 'Connected' : 'Not connected'}
                      </Badge>
                    </div>
                    <Separator className="bg-gray-200/60 dark:bg-gray-700/60" />
                    <div className="flex gap-2">
                      {!gateway.connected ? (
                        <Button onClick={() => connectGateway(gateway.id)} className="flex-1 gap-2">
                          <LinkIcon className="h-4 w-4" />
                          Connect
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          onClick={() =>
                            toast({
                              title: 'Opening gateway settings',
                              description: `${gateway.name} configuration panel`,
                            })
                          }
                          className="flex-1 gap-2"
                        >
                          <Settings className="h-4 w-4" />
                          Configure
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="settlement" className="space-y-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Card className="relative overflow-hidden border-gray-200/60 bg-gradient-to-br from-white to-gray-50/50 shadow-sm dark:border-gray-700/60 dark:from-gray-900 dark:to-gray-900/50">
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-transparent opacity-40" />
                <CardHeader className="relative pb-4">
                  <div className="flex items-start gap-3">
                    <div className="rounded-xl bg-gradient-to-br from-blue-100 to-blue-50 p-2.5 dark:from-blue-900/30 dark:to-blue-900/10">
                      <CalendarIcon className="h-5 w-5 text-blue-700 dark:text-blue-400" />
                    </div>
                    <div>
                      <CardTitle>Date Range</CardTitle>
                      <CardDescription className="mt-1 text-xs font-semibold uppercase tracking-wide">
                        Adjust settlement window
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="relative space-y-5">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        From
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="h-12 w-full justify-between bg-gray-50 text-left font-normal dark:bg-gray-800"
                          >
                            <span className="inline-flex items-center gap-2">
                              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                              {formatDateShort(exportStartDate)}
                            </span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={exportStartDate}
                            onSelect={setExportStartDate}
                            disabled={(date) => (exportEndDate && date > exportEndDate) || date > new Date()}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        To
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="h-12 w-full justify-between bg-gray-50 text-left font-normal dark:bg-gray-800"
                          >
                            <span className="inline-flex items-center gap-2">
                              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                              {formatDateShort(exportEndDate)}
                            </span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={exportEndDate}
                            onSelect={setExportEndDate}
                            disabled={(date) => (exportStartDate && date < exportStartDate) || date > new Date()}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <Button variant="outline" className="h-11 gap-2" onClick={() => handleQuickRange(7)}>
                      <RefreshCw className="h-4 w-4" />
                      Last 7 days
                    </Button>
                    <Button
                      className="h-11 gap-2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg dark:from-emerald-500 dark:to-emerald-600"
                      onClick={handleExportTransactions}
                      disabled={isExporting}
                    >
                      {isExporting ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4" />
                          Export CSV
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <Button variant="outline" onClick={() => handleQuickRange(0)}>
                      Today
                    </Button>
                    <Button variant="outline" onClick={() => handleQuickRange(7)}>
                      7 days
                    </Button>
                    <Button variant="outline" onClick={() => handleQuickRange(30)}>
                      30 days
                    </Button>
                  </div>

                  {exportStartDate && exportEndDate && (
                    <div className="rounded-lg border border-gray-200/60 bg-white/60 p-3 text-sm dark:border-gray-700/60 dark:bg-gray-900/60">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Settlement period</span>
                        <span className="font-semibold">
                          {getExportDaysCount()} day{getExportDaysCount() === 1 ? '' : 's'}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-gray-200/60 bg-gradient-to-br from-white to-gray-50/50 shadow-sm dark:border-gray-700/60 dark:from-gray-900 dark:to-gray-900/50">
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-emerald-500/5 via-transparent to-purple-500/5 opacity-40" />
                <CardHeader className="relative pb-4">
                  <div className="flex items-start gap-3">
                    <div className="rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 p-2.5 dark:from-emerald-900/30 dark:to-emerald-900/10">
                      <BarChart3 className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
                    </div>
                    <div>
                      <CardTitle>Settlement Summary</CardTitle>
                      <CardDescription className="mt-1.5">Expected payout overview</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="relative space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between rounded-lg bg-gray-50/60 p-3 dark:bg-gray-800/50">
                      <span className="text-sm text-muted-foreground">Gross revenue</span>
                      <span className="font-semibold">{formatCurrency(stats.totalRevenueCents, currency)}</span>
                    </div>
                    <div className="flex items-center justify-between rounded-lg bg-gray-50/60 p-3 dark:bg-gray-800/50">
                      <span className="text-sm text-muted-foreground">Processing fees</span>
                      <span className="font-semibold text-red-600 dark:text-red-400">
                        -{formatCurrency(stats.totalFeesCents, currency)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-lg bg-gray-50/60 p-3 dark:bg-gray-800/50">
                      <span className="text-sm text-muted-foreground">Refunds</span>
                      <span className="font-semibold text-red-600 dark:text-red-400">
                        -{formatCurrency(stats.refundedAmountCents, currency)}
                      </span>
                    </div>
                    <Separator className="bg-gray-200/60 dark:bg-gray-700/60" />
                    <div className="flex items-center justify-between rounded-xl border border-emerald-200/60 bg-gradient-to-br from-emerald-50 to-emerald-100/50 p-4 dark:border-emerald-700/30 dark:from-emerald-900/20 dark:to-emerald-900/10">
                      <span className="font-semibold text-emerald-900 dark:text-emerald-100">Net settlement</span>
                      <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                        {formatCurrency(
                          stats.totalRevenueCents - stats.totalFeesCents - stats.refundedAmountCents,
                          currency,
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2 border-t border-gray-200/60 pt-3 text-xs dark:border-gray-700/60">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Settlement status</span>
                      <Badge
                        variant="outline"
                        className="flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs text-blue-700 dark:bg-blue-900/20 dark:text-blue-400"
                      >
                        <Clock className="h-3 w-3" />
                        Pending
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Expected payout date</span>
                      <span className="font-medium">
                        {new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-gray-200/60 bg-white shadow-sm dark:border-gray-700/60 dark:bg-gray-900">
              <CardHeader className="flex flex-col gap-2 border-b border-gray-200/60 bg-gradient-to-br from-gray-50/50 to-transparent dark:border-gray-700/60 dark:from-gray-800/30 dark:to-transparent">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl">Settlement History</CardTitle>
                    <CardDescription>Recent payout cycles</CardDescription>
                  </div>
                  <Badge variant="outline" className="rounded-full bg-gray-100 px-3 py-1 text-xs dark:bg-gray-800">
                    Last 5 periods
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-200/60 hover:bg-transparent dark:border-gray-700/60">
                      <TableHead className="pl-6">Period</TableHead>
                      <TableHead>Transactions</TableHead>
                      <TableHead>Gross</TableHead>
                      <TableHead>Fees</TableHead>
                      <TableHead>Net</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="pr-6">Payout date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[
                      {
                        period: `${dateRange.from} - ${dateRange.to}`,
                        transactions: stats.totalTransactions,
                        grossCents: stats.totalRevenueCents + stats.pendingAmountCents,
                        feesCents: stats.totalFeesCents,
                        status: 'Pending' as const,
                        payout: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
                      },
                      {
                        period: 'Previous period',
                        transactions: Math.max(0, stats.totalTransactions - 8),
                        grossCents: Math.max(0, stats.totalRevenueCents - 35000),
                        feesCents: Math.max(0, stats.totalFeesCents - 1500),
                        status: 'Completed' as const,
                        payout: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
                      },
                      {
                        period: 'Two periods ago',
                        transactions: Math.max(0, stats.totalTransactions - 16),
                        grossCents: Math.max(0, stats.totalRevenueCents - 62000),
                        feesCents: Math.max(0, stats.totalFeesCents - 2400),
                        status: 'Completed' as const,
                        payout: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
                      },
                    ].map((settlement, index) => (
                      <TableRow
                        key={index}
                        className="border-gray-200/60 transition-colors hover:bg-gray-50/50 dark:border-gray-700/60 dark:hover:bg-gray-800/50"
                      >
                        <TableCell className="pl-6 font-medium">{settlement.period}</TableCell>
                        <TableCell>{settlement.transactions}</TableCell>
                        <TableCell className="font-semibold">
                          {formatCurrency(settlement.grossCents, currency)}
                        </TableCell>
                        <TableCell className="text-red-600 dark:text-red-400">
                          -{formatCurrency(settlement.feesCents, currency)}
                        </TableCell>
                        <TableCell className="font-semibold text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(settlement.grossCents - settlement.feesCents, currency)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ${
                              settlement.status === 'Completed'
                                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                                : 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                            }`}
                          >
                            {settlement.status === 'Completed' ? (
                              <CheckCircle2 className="h-3 w-3" />
                            ) : (
                              <Clock className="h-3 w-3" />
                            )}
                            {settlement.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="pr-6">
                          {settlement.payout.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog
          open={!!selectedTransaction && !showRefundDialog}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedTransaction(null);
            }
          }}
        >
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Transaction details
              </DialogTitle>
              <DialogDescription>
                Complete information for transaction {selectedTransaction?.id}
              </DialogDescription>
            </DialogHeader>
            {selectedTransaction && (
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Transaction ID</Label>
                      <p className="mt-1 font-mono text-sm">{selectedTransaction.id}</p>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Order ID</Label>
                      <p className="mt-1 font-mono text-sm">{selectedTransaction.orderId}</p>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Customer</Label>
                      <p className="mt-1 text-sm font-medium">{selectedTransaction.customerName}</p>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Created at</Label>
                      <p className="mt-1 text-sm">
                        {selectedTransaction.timestamp.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Amount</Label>
                      <p className="mt-1 text-2xl font-bold">
                        {formatCurrency(selectedTransaction.amountCents, currency)}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Method</Label>
                      <div className="mt-1 inline-flex items-center gap-2 rounded-md bg-gray-100 px-2.5 py-1.5 text-sm dark:bg-gray-800">
                        {getPaymentMethodIcon(selectedTransaction.methodCategory)}
                        <span className="capitalize">{selectedTransaction.methodCategory}</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Status</Label>
                      <div className="mt-1">{getStatusBadge(selectedTransaction.status)}</div>
                    </div>
                    {selectedTransaction.raw.refundReason && (
                      <div>
                        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Refund reason</Label>
                        <p className="mt-1 text-sm">{selectedTransaction.raw.refundReason}</p>
                      </div>
                    )}
                    {selectedTransaction.raw.processedBy && (
                      <div>
                        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Processed by</Label>
                        <p className="mt-1 text-sm">{selectedTransaction.raw.processedBy}</p>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Estimated fee</Label>
                      <p className="mt-1 text-sm">
                        {formatCurrency(selectedTransaction.estimatedFeeCents, currency)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedTransaction(null)}>
                Close
              </Button>
              <Button
                onClick={() => selectedTransaction && handleReceiptAction(selectedTransaction, 'email')}
                disabled={!!selectedTransaction && activeReceiptPaymentId === selectedTransaction.id}
                className="gap-2"
              >
                {selectedTransaction && activeReceiptPaymentId === selectedTransaction.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                Send receipt
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={showRefundDialog}
          onOpenChange={(open) => {
            setShowRefundDialog(open);
            if (!open) {
              setRefundReason('');
              setSelectedTransaction(null);
            }
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <RotateCcw className="h-5 w-5" />
                Issue refund
              </DialogTitle>
              <DialogDescription>
                Process a refund for transaction {selectedTransaction?.id}
              </DialogDescription>
            </DialogHeader>
            {selectedTransaction && (
              <div className="space-y-4 py-4">
                <div className="rounded-lg border border-gray-200/60 bg-gray-50/60 p-4 dark:border-gray-700/60 dark:bg-gray-800/60">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Refund amount</span>
                    <span className="text-2xl font-bold">
                      {formatCurrency(selectedTransaction.amountCents, currency)}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Customer</span>
                    <span className="font-medium">{selectedTransaction.customerName}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="refund-reason">Refund reason *</Label>
                  <Textarea
                    id="refund-reason"
                    placeholder="Provide context for the refund..."
                    value={refundReason}
                    onChange={(event) => setRefundReason(event.target.value)}
                    rows={4}
                    className="resize-none"
                  />
                </div>
                <div className="rounded-lg border border-red-200/60 bg-red-50 p-3 text-xs text-red-700 dark:border-red-800/40 dark:bg-red-950/30 dark:text-red-300">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4" />
                    <p>This action can't be undone. Funds will return to the original payment method.</p>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRefundDialog(false)}>
                Cancel
              </Button>
              <Button
                className="bg-red-600 text-white hover:bg-red-700"
                onClick={handleRefund}
                disabled={!refundReason.trim() || isRefunding}
              >
                {isRefunding ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="mr-2 h-4 w-4" />
                )}
                {isRefunding ? 'Processing...' : 'Process refund'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Sheet
          open={!!customerHistoryTarget}
          onOpenChange={(open) => {
            if (!open) {
              setCustomerHistoryTarget(null);
            }
          }}
        >
          <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
            <SheetHeader>
              <SheetTitle>
                {t('payments:historySheet.title', { defaultValue: 'Customer history' })}
              </SheetTitle>
              <SheetDescription>
                {t('payments:historySheet.description', {
                  defaultValue: 'Recent payments associated with {{name}}.',
                  name: historyCustomerName,
                })}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-6 space-y-6">
              <div className="space-y-4 rounded-xl border border-gray-200/60 bg-gray-50/60 p-4 dark:border-gray-700/60 dark:bg-gray-900/40">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('payments:historySheet.rangeLabel', { defaultValue: 'History range' })}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t('payments:historySheet.rangeHint', {
                      defaultValue: 'Adjust this range to refetch matching payments.',
                    })}
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {t('payments:historySheet.fromLabel', { defaultValue: 'From' })}
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="justify-between text-left font-normal"
                        >
                          <span className="inline-flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                            {historyRangeFromDate
                              ? formatDateShort(historyRangeFromDate)
                              : t('payments:historySheet.presets.all', { defaultValue: 'All time' })}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={historyRangeFromDate}
                          onSelect={(date) => handleCustomerHistoryDateSelect('from', date ?? undefined)}
                          disabled={(date) =>
                            (historyRangeToDate && date > historyRangeToDate) || date > new Date()
                          }
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {t('payments:historySheet.toLabel', { defaultValue: 'To' })}
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="justify-between text-left font-normal"
                        >
                          <span className="inline-flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                            {historyRangeToDate
                              ? formatDateShort(historyRangeToDate)
                              : t('payments:historySheet.presets.all', { defaultValue: 'All time' })}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={historyRangeToDate}
                          onSelect={(date) => handleCustomerHistoryDateSelect('to', date ?? undefined)}
                          disabled={(date) =>
                            (historyRangeFromDate && date < historyRangeFromDate) || date > new Date()
                          }
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(
                    [
                      { key: 'today', label: t('payments:historySheet.presets.today', { defaultValue: 'Today' }) },
                      { key: '7d', label: t('payments:historySheet.presets.sevenDays', { defaultValue: '7 days' }) },
                      { key: '30d', label: t('payments:historySheet.presets.thirtyDays', { defaultValue: '30 days' }) },
                      { key: '90d', label: t('payments:historySheet.presets.ninetyDays', { defaultValue: '90 days' }) },
                      { key: 'all', label: t('payments:historySheet.presets.all', { defaultValue: 'All time' }) },
                    ] as const
                  ).map((preset) => (
                    <Button
                      key={preset.key}
                      size="sm"
                      variant="outline"
                      onClick={() => handleCustomerHistoryPreset(preset.key)}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
              </div>
              {customerHistoryQuery.isLoading ? (
                <div className="space-y-3">
                  {[0, 1, 2].map((index) => (
                    <Skeleton key={index} className="h-20 w-full rounded-xl" />
                  ))}
                </div>
              ) : customerHistoryQuery.isError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>
                    {t('payments:historySheet.errorTitle', {
                      defaultValue: 'Unable to load history',
                    })}
                  </AlertTitle>
                  <AlertDescription>
                    {t('payments:historySheet.errorDescription', {
                      defaultValue: 'Please try again.',
                    })}
                  </AlertDescription>
                </Alert>
              ) : customerHistoryTransactions.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-muted-foreground dark:border-gray-600">
                  {t('payments:historySheet.empty', {
                    defaultValue: 'No matching payments found in this date range.',
                  })}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200/60 bg-gray-50/60 p-4 dark:border-gray-700/60 dark:bg-gray-900/40 sm:grid-cols-3">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        {t('payments:historySheet.totals.spend', { defaultValue: 'Lifetime spend' })}
                      </p>
                      <p className="mt-1 text-lg font-semibold">
                        {formatCurrency(customerHistorySummary.totalCents, currency)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        {t('payments:historySheet.totals.transactions', {
                          defaultValue: 'Transactions',
                        })}
                      </p>
                      <p className="mt-1 text-lg font-semibold">{customerHistorySummary.count}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        {t('payments:historySheet.totals.lastVisit', {
                          defaultValue: 'Last visit',
                        })}
                      </p>
                      <p className="mt-1 text-lg font-semibold">
                        {customerHistorySummary.lastVisit
                          ? formatDate(customerHistorySummary.lastVisit)
                          : t('payments:historySheet.lastVisitEmpty', { defaultValue: 'N/A' })}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3 pb-6">
                    {customerHistoryTransactions.map((historyTransaction) => {
                      const orderHref = buildTenantHref(`orders/${historyTransaction.orderId}`);
                      return (
                        <div
                          key={`${historyTransaction.id}-${historyTransaction.timestamp.getTime()}`}
                          className="rounded-xl border border-gray-200/60 bg-white p-4 shadow-sm dark:border-gray-700/60 dark:bg-gray-900"
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <p className="text-sm font-semibold">
                                {formatCurrency(historyTransaction.amountCents, currency)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {formatDate(historyTransaction.timestamp)} ·{' '}
                                {formatTime(historyTransaction.timestamp)}
                              </p>
                            </div>
                            <div className="inline-flex items-center gap-2 text-xs capitalize text-muted-foreground">
                              {getPaymentMethodIcon(historyTransaction.methodCategory)}
                              {historyTransaction.methodCategory}
                            </div>
                          </div>
                          <div className="mt-4 flex items-center justify-between text-sm">
                            <Link
                              to={orderHref}
                              className="inline-flex items-center gap-1 text-primary hover:underline"
                              onClick={() => setCustomerHistoryTarget(null)}
                            >
                              <ArrowUpRight className="h-3.5 w-3.5" />
                              #{historyTransaction.orderId}
                            </Link>
                            <div>{getStatusBadge(historyTransaction.status)}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
    </div>
  );
};

export default PaymentsPage;
